import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, AlertTriangle } from "lucide-react";
import { fetchQuizResults, fetchQuizHistoryResults } from "@/lib/supabase";
import Results from "@/components/quiz/Results";
import { useAuthSession } from "@/hooks/useAuthSession";
import { Card, CardContent } from "@/components/ui/card";
import type { QuizResultsData, Question } from "@/types/quiz";
import { normalizeDifficulty } from "@/types/quiz";
import { toast } from "sonner";
import { useQuizSessionHistory } from "@/hooks/useQuizSessionHistory";

const QuizHistoryDetail = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quizData, setQuizData] = useState<QuizResultsData | null>(null);
  const { userId } = useAuthSession();
  const navigate = useNavigate();
  const { resetSubmissionStatus, resetSnapshots } = useQuizSessionHistory();

  useEffect(() => {
    resetSubmissionStatus();
    resetSnapshots();
  }, [resetSubmissionStatus, resetSnapshots]);

  const processQuestions = (questions: any[]): Question[] => {
    if (!Array.isArray(questions)) {
      console.error("processQuestions: Not an array:", questions);
      return [];
    }

    console.log("Processing questions:", questions.length);
    
    // Create a deep copy to avoid reference issues
    const questionsCopy = JSON.parse(JSON.stringify(questions));

    // Create a map to deduplicate questions by ID
    const uniqueQuestionsMap = new Map();
    
    questionsCopy.forEach((q, index) => {
      if (!q || typeof q !== 'object') {
        console.warn(`Question at index ${index} is invalid:`, q);
        return;
      }
      
      const questionId = q.id || `question-${index}-${Math.random().toString(36).substring(2, 9)}`;
      
      // Only add if this question ID hasn't been seen yet
      if (!uniqueQuestionsMap.has(questionId)) {
        // Normalize difficulty to ensure it's one of the allowed values
        const normalizedDifficulty = normalizeDifficulty(q.difficulty || 'medium');
        
        let safeQuestionType: "mcq" | "true_false" | "fill_blank" = "mcq";
        if (q.question_type === "mcq" || q.question_type === "true_false" || q.question_type === "fill_blank") {
          safeQuestionType = q.question_type;
        }
        
        uniqueQuestionsMap.set(questionId, {
          ...q,
          id: questionId,
          difficulty: normalizedDifficulty,
          question_type: safeQuestionType
        });
      }
    });
    
    return Array.from(uniqueQuestionsMap.values());
  };

  useEffect(() => {
    const loadQuizResults = async () => {
      if (!sessionId) {
        setError("Session ID is missing");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Loading quiz results for session:', sessionId);
        
        const historyResults = await fetchQuizHistoryResults(sessionId);
        
        if (historyResults && Array.isArray(historyResults.questions) && historyResults.questions.length > 0) {
          console.log('Loaded quiz history results:', historyResults);
          console.log('History results questions count:', historyResults.questions.length);
          
          // Process questions to remove duplicates
          const processedQuestions = processQuestions(historyResults.questions);
          
          console.log('Processed history data questions:', processedQuestions.length);
          
          // Ensure answers array matches processed questions
          let validAnswers = [...(Array.isArray(historyResults.answers) ? historyResults.answers : [])];
          
          // Trim/pad answers array as needed
          while (validAnswers.length < processedQuestions.length) {
            validAnswers.push(null);
          }
          if (validAnswers.length > processedQuestions.length) {
            validAnswers = validAnswers.slice(0, processedQuestions.length);
          }
          
          const typedHistoryData: QuizResultsData = {
            questions: processedQuestions,
            answers: validAnswers,
            subject: historyResults.subject,
            category: historyResults.category,
            difficulty: historyResults.difficulty,
            score: historyResults.score || 0,
            total_questions: historyResults.total_questions || processedQuestions.length,
            percentage: historyResults.percentage || 0
          };
          
          setQuizData(typedHistoryData);
        } else {
          console.log('No history results found, falling back to quiz_results');
          const results = await fetchQuizResults(sessionId);
          
          if (results && Array.isArray(results.questions) && results.questions.length > 0) {
            console.log('Loaded quiz results:', results);
            console.log('Results questions count:', results.questions.length);
            console.log('Results answers count:', results.answers?.length || 0);
            console.log('Result question difficulties:', results.questions.map(q => q.difficulty));
            
            // Deep copy the questions to avoid reference issues
            const safeQuestions = JSON.parse(JSON.stringify(results.questions));
            
            console.log("Safe questions from fallback after copying:", safeQuestions.length);
            
            // Ensure answers array is at least as long as questions array
            let validAnswers = [...(Array.isArray(results.answers) ? results.answers : [])];
            while (validAnswers.length < safeQuestions.length) {
              validAnswers.push(null);
            }
            
            // Trim excess answers if needed
            if (validAnswers.length > safeQuestions.length) {
              validAnswers = validAnswers.slice(0, safeQuestions.length);
            }
            
            console.log('After preprocessing fallback - Questions:', safeQuestions.length, 'Answers:', validAnswers.length);
            
            // Process questions WITHOUT filtering by difficulty
            const processedQuestions = processQuestions(safeQuestions);
            
            console.log('Processed results data questions:', processedQuestions.length);
            console.log('Processed results data answers:', validAnswers.length);
            console.log('Question IDs:', processedQuestions.map(q => q.id));
            console.log('Question difficulties after processing:', processedQuestions.map(q => q.difficulty));
            
            const typedResultsData: QuizResultsData = {
              questions: processedQuestions,
              answers: validAnswers,
              subject: results.subject,
              category: results.category,
              difficulty: results.difficulty,
              score: results.score || 0,
              total_questions: results.total_questions || processedQuestions.length,
              percentage: results.percentage || 0
            };
            
            setQuizData(typedResultsData);
          } else {
            console.error('Could not find any quiz results for session:', sessionId);
            setError("Could not find quiz results for this session");
          }
        }
      } catch (err) {
        console.error('Error loading quiz results:', err);
        setError("Failed to load quiz results. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadQuizResults();
  }, [sessionId]);

  const handleBackToHistory = () => {
    navigate("/quiz/history");
  };

  if (loading) {
    return (
      <Card className="container mx-auto py-6 max-w-4xl">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-gray-500">Loading quiz results...</p>
        </CardContent>
      </Card>
    );
  }

  if (error || !quizData || !quizData.questions || quizData.questions.length === 0) {
    return (
      <Card className="container mx-auto py-6 max-w-4xl">
        <CardContent className="text-center py-12">
          <div className="text-red-500 mb-4">
            <AlertTriangle className="h-12 w-12 mx-auto mb-2" />
            {error || "No quiz data found"}
          </div>
          <Button 
            variant="outline" 
            onClick={handleBackToHistory}
            className="flex items-center"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Quiz History
          </Button>
        </CardContent>
      </Card>
    );
  }

  console.log('Rendering Results with:', {
    questionCount: quizData.questions.length,
    answerCount: quizData.answers?.length || 0,
    subject: quizData.subject,
    category: quizData.category,
    difficulty: quizData.difficulty,
    questionIds: quizData.questions.map(q => q.id),
    questionDifficulties: quizData.questions.map(q => q.difficulty)
  });

  // Make a deep copy to ensure we don't mutate original data
  const completeQuestions = JSON.parse(JSON.stringify(quizData.questions));
  
  console.log('Complete questions before rendering:', completeQuestions.length);
  console.log('Question difficulties before rendering:', completeQuestions.map(q => q.difficulty));
  
  // Create a new copy of the answers array
  let validAnswers = [...(Array.isArray(quizData.answers) ? quizData.answers : [])];
  
  // Ensure the answers array is at least as long as the questions array
  while (validAnswers.length < completeQuestions.length) {
    validAnswers.push(null);
  }
  
  // Trim excess answers if needed
  if (validAnswers.length > completeQuestions.length) {
    validAnswers = validAnswers.slice(0, completeQuestions.length);
  }
  
  console.log(`Final validation: questions=${completeQuestions.length}, answers=${validAnswers.length}`);
  
  // Log each question for debugging
  completeQuestions.forEach((q: Question, idx: number) => {
    console.log(`Question ${idx}: id=${q.id}, difficulty=${q.difficulty}, answer=${validAnswers[idx]}`);
  });

  console.log("Final check - all questions and difficulties:", 
    completeQuestions.map((q: Question, i: number) => `Q${i}:${q.difficulty}:${q.question?.substring(0, 15)}...`)
  );

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      <div className="mb-6">
        <Button 
          variant="outline" 
          onClick={handleBackToHistory}
          className="mb-4 flex items-center"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Quiz History
        </Button>
      </div>

      <Results 
        questions={completeQuestions} 
        answers={validAnswers} 
        onReset={handleBackToHistory} 
        sessionId={sessionId} 
        subject={quizData.subject}
        category={quizData.category}
        difficulty={quizData.difficulty}
      />
    </div>
  );
};

export default QuizHistoryDetail;
